// Shared design tokens for the admin dashboard.
// Every dashboard section imports from here so the whole page reads as ONE system
// instead of a set of separately-styled blocks with clashing colours.
//
// Aesthetic: FLAT + LIGHT. No drop shadows, no gradients. Surfaces are white or
// soft pastel, separated only by hairline borders. A single brand accent.

// --- Surfaces --------------------------------------------------------------
// The page has a soft, animated AI "aurora" behind it. Panels are frosted glass
// — translucent white with a heavy backdrop blur and a whisper-thin ring — so
// the ambient colour drifts gently behind the content. No boxy borders, no
// heavy shadows: clean, minimal, and quietly "powered by AI".
export const pageBg = "bg-[#F7F8FC]";
export const card = "bg-white/70 backdrop-blur-xl rounded-2xl ring-1 ring-gray-900/[0.05]";

// A compact frosted chip (store link, small actions).
export const chip =
  "bg-white/70 backdrop-blur-xl ring-1 ring-gray-900/[0.05] rounded-full";

// Optional hover for interactive cards.
export const cardHover = "transition-colors duration-200 hover:bg-white/90";

// Soft inner divider for card headers / list separators (barely-there line).
export const divider = "border-gray-100";

// Consistent inner padding for card bodies.
export const cardPad = "p-4 sm:p-5";

// --- Typography ------------------------------------------------------------
export const sectionTitle = "text-base sm:text-lg font-semibold text-gray-900";
export const sectionSub = "text-xs sm:text-sm text-gray-500";
export const eyebrow = "text-[11px] font-semibold text-gray-400 uppercase tracking-wider";

// --- Brand accent ----------------------------------------------------------
// A single brand accent (indigo) used for every primary action, active state,
// link and chart primary. Nothing else should introduce a competing "brand".
export const accent = {
  text: "text-indigo-600",
  // Flat, light primary button.
  solid: "bg-indigo-600 text-white hover:bg-indigo-700",
  // Flat, light secondary surface (borderless).
  soft: "bg-indigo-50 text-indigo-700",
  hex: "#4F46E5",
};

// A neutral, low-noise pill for metadata labels ("Last 7 days", "Real-time").
export const pill =
  "inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full";

// --- Stat icon tints -------------------------------------------------------
// One saturation level across all four so the KPI row looks like a set, not
// four unrelated swatches. Each is a flat, light `bg text` badge.
export const statTints = {
  emerald: "bg-emerald-50 text-emerald-600",
  indigo: "bg-indigo-50 text-indigo-600",
  violet: "bg-violet-50 text-violet-600",
  amber: "bg-amber-50 text-amber-600",
} as const;

// --- Chart palette ---------------------------------------------------------
// Order-status colours reused by the chart and any status legends. Flat, light.
export const statusColors = {
  confirmed: "#34D399", // emerald-400
  pending: "#FBBF24", // amber-400
  cancel: "#FB7185", // rose-400
} as const;
